import {
  DROP_EXPLAINER,
  DROP_HINT_END,
  METHOD_TYPES,
  methodLabel,
  RHYTHMS,
  stepValid,
  WEEKDAYS
} from '@/config/fulfillmentWizardSteps'
import { formatClock, formatDate, formatLongDate, formatTime } from '@/helpers/dropWizardDates'
import { NOT_ADDED, NOT_CHOSEN } from '@/helpers/wizardRail'

const RECEIVE_VERBS = {
  pickup: 'pick up',
  delivery: 'get their delivery',
  shipping: 'have their order shipped',
  uber_direct: 'get their courier delivery'
}

const ARRIVAL_LABELS = { pickup: 'Pickup', delivery: 'Delivery', shipping: 'Ships', uber_direct: 'Courier' }

const stamp = (iso, tz) => `${formatDate(iso, tz)} at ${formatTime(iso, tz)}`
const badge = (iso, tz) => `Opens ${formatDate(iso, tz)}, ${formatTime(iso, tz)}`

export const openOccurrences = preview => (preview?.occurrences ?? []).filter(occurrence => !occurrence.closed)

export const nextDateChips = preview =>
  (preview?.occurrences ?? []).map(occurrence => {
    const date = formatDate(occurrence.arrival_date)
    return { label: occurrence.closed ? `${date} closed` : date, closed: Boolean(occurrence.closed) }
  })

export const occurrenceRows = (occurrence, { type, tz }) => {
  const production = { key: 'Production', value: formatDate(occurrence.production_date) }
  if (occurrence.closed) return [production, { key: 'Ordering', value: 'Closed day, no ordering' }]
  const opens = occurrence.opens_at
    ? `${formatDate(occurrence.opens_at, tz)}, ${formatTime(occurrence.opens_at, tz)}`
    : 'Now'
  const closes = `${formatDate(occurrence.closes_at, tz)}, ${formatTime(occurrence.closes_at, tz)}`
  const [start, end] = occurrence.arrival
  return [
    { key: 'Ordering', value: `${opens} to ${closes}` },
    production,
    {
      key: ARRIVAL_LABELS[type] ?? 'Arrives',
      value: `${formatDate(occurrence.arrival_date)}, ${formatTime(start, tz)} to ${formatTime(end, tz)}`
    }
  ]
}

export const dateCards = (occurrences, context) =>
  occurrences.map(occurrence => ({
    number: Number(occurrence.arrival_date.slice(-2)),
    title: formatLongDate(occurrence.arrival_date),
    rows: occurrenceRows(occurrence, context)
  }))

export const describeDropExample = (occurrence, { type, tz }) => {
  if (!occurrence) return ''
  const opens = occurrence.opens_at ? `on ${stamp(occurrence.opens_at, tz)}` : 'now'
  const [start, end] = occurrence.arrival
  const receive = RECEIVE_VERBS[type] ?? 'get their order'
  return `For example, with this setup a drop made on ${formatDate(occurrence.production_date)} would open ordering ${opens}, remind customers to order, close ordering on ${stamp(occurrence.closes_at, tz)} and charge any auto-charge carts at that moment. Customers would then ${receive} on ${formatDate(occurrence.arrival_date)} between ${formatTime(start, tz)} and ${formatTime(end, tz)}.`
}

export const describeOpens = (preview, { data, tz }) => {
  const open = openOccurrences(preview)
  const [first] = open
  if (!first) return ''
  const later = open.find(occurrence => occurrence.opens_at)
  const saved = `as soon as ${methodLabel(data)} is saved`
  if (!later) {
    const dates = open.length === 1 ? formatDate(first.arrival_date) : 'every date'
    return `Customers can order for ${dates} ${saved}.`
  }
  const waiting = `${formatDate(later.arrival_date)} shows at checkout as "${badge(later.opens_at, tz)}" until then and cannot be ordered for.`
  return first.opens_at ? waiting : `Customers can order for ${formatDate(first.arrival_date)} ${saved}. ${waiting}`
}

export const dropExplainer = wizard =>
  [
    DROP_EXPLAINER,
    describeDropExample(openOccurrences(wizard.preview.value)[0], {
      type: wizard.data.type,
      tz: wizard.timeZone.value
    }),
    DROP_HINT_END
  ]
    .filter(Boolean)
    .join(' ')

export const formatMoney = (cents, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((cents ?? 0) / 100)

const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1)
const joinNames = names =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
const weekdayLong = value => WEEKDAYS.find(w => w.value === value)?.long
const weekdayShort = value => WEEKDAYS.find(w => w.value === value)?.label
const sortedWeekdays = days => [...WEEKDAYS].filter(w => days.includes(w.value)).map(w => w.value)

const verbFor = type => ({ pickup: 'collect', delivery: 'get', shipping: 'get', uber_direct: 'get' })[type] ?? 'get'

const whereText = (data, country) => {
  if (['pickup', 'uber_direct'].includes(data.type)) {
    const a = data.address
    return a.address1 ? [a.address1, a.city].filter(Boolean).join(', ') : ''
  }
  const z = data.zone
  if (z.kind === 'map') return z.waypoints.length >= 3 ? 'the area on the map' : ''
  if (z.kind === 'zips')
    return z.zipCodes.length ? `${z.zipCodes.length} zip code${z.zipCodes.length === 1 ? '' : 's'}` : ''
  if (z.kind === 'states') return z.states.length ? joinNames(z.states) : ''
  return `Anywhere in ${country}`
}

const whereSentence = (data, where) => {
  if (data.type === 'pickup') return `from ${where}`
  if (data.type === 'uber_direct') return `by Uber courier from ${where}`
  if (data.type === 'shipping') return `shipped to ${where.replace(/^Anywhere/, 'anywhere')}`
  return `delivered to ${where}`
}

const RHYTHM_PREFIXES = { weekly: 'every', biweekly: 'every other', four_weekly: 'every fourth' }

const daysText = schedule => {
  const rhythm = RHYTHMS.find(r => r.value === schedule.rhythm)
  if (!rhythm) return ''
  if (rhythm.value === 'once') return schedule.date ? `${rhythm.title} on ${formatDate(schedule.date)}` : ''
  const days = sortedWeekdays(schedule.weekdays)
  return days.length ? `${rhythm.title} on ${days.map(weekdayShort).join(', ')}` : ''
}

const daysSentence = schedule => {
  if (schedule.rhythm === 'once') return schedule.date ? `on ${formatLongDate(schedule.date)}` : ''
  const names = sortedWeekdays(schedule.weekdays).map(weekdayLong)
  const prefix = RHYTHM_PREFIXES[schedule.rhythm]
  return prefix && names.length ? `${prefix} ${joinNames(names)}` : ''
}

const windowText = arrival => `${formatClock(arrival.start)} to ${formatClock(arrival.end)}`
const productionPhrase = daysBefore => {
  if (!daysBefore) return 'the same day'
  return daysBefore === 1 ? 'the day before' : `${daysBefore} days before`
}
const productionText = daysBefore => `made ${productionPhrase(daysBefore)}`
const closesText = closes => {
  const when =
    closes.daysBefore === 0
      ? 'the morning of'
      : closes.daysBefore === 1
        ? 'the day before'
        : `${closes.daysBefore} days before`
  return `${when}, ${formatClock(closes.time)}`
}
const opensText = opens => {
  if (opens.kind === 'always') return 'right away'
  if (opens.kind === 'days_before') return `${opens.days} days before`
  return 'when the previous one closes'
}
const closesSentence = closes => `${closesText(closes).replace(', ', ' production at ')}`
const costText = (pricing, currency) => {
  const fee = pricing.priceCents > 0 ? formatMoney(pricing.priceCents, currency) : 'Free'
  const tax = pricing.priceCents > 0 && pricing.taxable ? ' plus tax' : ''
  const min = pricing.minimumOrderCents ? `, minimum order ${formatMoney(pricing.minimumOrderCents, currency)}` : ''
  return `${fee}${tax}${min}`
}

const chosenStoreNames = (data, stores) =>
  data.storeIds.map(id => stores.find(s => String(s.id) === String(id))?.name).filter(Boolean)

export const describeSetup = (data, { stores = [], country = 'USA', currency = 'USD' } = {}) => {
  const type = METHOD_TYPES.find(t => t.value === data.type)
  const storeNames = chosenStoreNames(data, stores)
  const label = methodLabel(data)
  const where = whereText(data, country)
  const s = data.schedule
  const defaults = {
    times: s.arrival.preset === 'all_day' && !s.productionDaysBefore,
    ordering: s.closes.preset === 'day_before' && s.opens.kind === 'previous' && s.limit === null,
    pricing: data.pricing.priceCents === 0 && !data.pricing.taxable && data.pricing.minimumOrderCents === null
  }

  const rows = [
    {
      key: 'method',
      label: 'Method',
      value: type ? `${type.label} · ${data.name || ''}`.trim() : NOT_CHOSEN,
      set: !!type
    },
    { key: 'where', label: 'Where', value: where || NOT_ADDED, set: !!where },
    { key: 'stores', label: 'Stores', value: storeNames.join(', ') || NOT_CHOSEN, set: storeNames.length > 0 },
    { key: 'days', label: 'Days', value: daysText(s) || NOT_CHOSEN, set: !!daysText(s) },
    {
      key: 'times',
      label: 'Time',
      value: `${windowText(s.arrival)}, ${productionText(s.productionDaysBefore)}`,
      set: true,
      isDefault: defaults.times
    },
    {
      key: 'ordering',
      label: 'Ordering',
      value: `Opens ${opensText(s.opens)}. Closes ${closesText(s.closes)}${s.limit ? `. Up to ${s.limit} orders` : ''}`,
      set: true,
      isDefault: defaults.ordering
    },
    {
      key: 'pricing',
      label: 'Cost',
      value: costText(data.pricing, currency),
      set: true,
      isDefault: defaults.pricing
    }
  ].map(row => ({ ...row, valid: stepValid(row.key, data) }))

  const cost = data.pricing.priceCents > 0 ? `costs ${costText(data.pricing, currency)}` : 'is free'
  const sentence =
    type && where && daysSentence(s)
      ? `Customers ${verbFor(data.type)} ${label} ${whereSentence(data, where)} ${daysSentence(s)} between ${formatClock(s.arrival.start)} and ${formatClock(s.arrival.end)}. You make the orders ${productionPhrase(s.productionDaysBefore)}. Ordering opens ${opensText(s.opens)} and closes ${closesSentence(s.closes)}. ${capitalized(label)} ${cost} and is offered on ${joinNames(storeNames) || 'no store yet'}.`
      : ''

  return { sentence, rows }
}

const METHOD_TEXT = {
  pickup: where => `Customers pick up from ${where}.`,
  uber_direct: where => `An Uber courier collects from ${where} and delivers nearby.`,
  delivery: where => `You deliver to ${where}.`,
  shipping: where => `You ship to ${where.replace(/^Anywhere/, 'anywhere')}.`
}

const slotItem = schedule => {
  const once = schedule.rhythm === 'once'
  const repeat = once
    ? 'Does not repeat.'
    : `${RHYTHMS.find(r => r.value === schedule.rhythm).title.replace('Every', 'Repeats every')}. Bottle keeps six months of dates ahead and adds more as time passes.`
  const limit = schedule.limit ? `Up to ${schedule.limit} orders each time.` : 'No order limit.'
  return {
    title: once ? `One slot ${daysSentence(schedule)}` : `A repeating slot ${daysSentence(schedule)}`,
    text: `${capitalized(windowText(schedule.arrival))}, made ${productionPhrase(schedule.productionDaysBefore)}. Ordering opens ${opensText(schedule.opens)} and closes ${closesSentence(schedule.closes)}. ${limit} ${repeat}`
  }
}

const feeItem = (pricing, currency) => {
  const tax = pricing.taxable ? ' plus tax' : ''
  return {
    title: pricing.priceCents > 0 ? `A ${formatMoney(pricing.priceCents, currency)} fee${tax}` : 'No fee',
    text: pricing.minimumOrderCents
      ? `Minimum order ${formatMoney(pricing.minimumOrderCents, currency)}. Below the minimum, customers cannot check out.`
      : 'No minimum order.'
  }
}

export const whatWeSetUp = (data, { stores = [], country = 'USA', currency = 'USD' } = {}) => {
  const label = methodLabel(data)
  const names = chosenStoreNames(data, stores)
  return [
    {
      title: data.name.trim() ? `A fulfillment method called ${label}` : 'A new fulfillment method',
      text: METHOD_TEXT[data.type]?.(whereText(data, country)) ?? ''
    },
    {
      title: `Offered on ${joinNames(names) || 'no store yet'}`,
      text: `Customers see ${label} at checkout on ${names.length === 1 ? 'that store' : 'those stores'} only.`
    },
    slotItem(data.schedule),
    feeItem(data.pricing, currency)
  ]
}
